/**
 * Shared address helpers for the Dynamic Template Provider (MD-148).
 *
 * These mirror the NewRx template logic on the Patient page so the calendar
 * template flow resolves the same "practice vs. home-visit" addresses.
 *
 * The one thing to know before touching this file: the practice address is NOT
 * a patient address. On the server `Address` hangs off EITHER a patient
 * (`Address.patient`) or a location (`Address.location`), and the practice is
 * the location's. So `patient.addresses` never contains it, and every consumer
 * has to merge the current location's address in first — see
 * `mergePracticeAddress` and the `useTemplateAddresses` hook that applies it.
 */

import { AddressDto, AddressDtoTypeEnum } from "../../../api-client";

/**
 * Does this Rx schedule at the practice?
 *
 * `isHomeVisit` is nullable, and only an explicit `true` licenses a home visit:
 * an unset flag means the Rx was never marked for house calls, which is a
 * practice appointment. Anything else would leave "not set" without a visit
 * type, and every appointment must have an address.
 */
export const isPracticeRx = (isHomeVisit: boolean | null | undefined): boolean =>
  isHomeVisit !== true;

/**
 * The practice ("Praxis") address out of an already-merged address list.
 *
 * Selected by `type === "Praxis"` — the same discriminator NewRx and
 * AppointmentEditCard use. The previous flag heuristic
 * (`!isPrimary && !isVisit && !isAlternativeBilling`) could only ever match a
 * PATIENT address that happened to carry no flags, and never the practice,
 * because the practice address is not in `patient.addresses` at all.
 */
export const pickPracticeAddress = (
  addresses: AddressDto[]
): AddressDto | undefined =>
  addresses.find((a) => a.type === AddressDtoTypeEnum.Praxis);

/**
 * Merge the current location's address into a patient's addresses as the
 * practice option, so the practice is ALWAYS offerable — both as the automatic
 * pick for a practice Rx and as a selectable alternative for a home-visit Rx.
 *
 * The location address is stamped `type: Praxis`: `Address.type` defaults to
 * `Haus` at the column level, so a location row whose type was never set would
 * otherwise masquerade as a house-visit address and drag a visit fee with it.
 * Being the location's address is what makes it the practice; the column is
 * just how that is spelled downstream (`feeCodeForAddress`, the option labels).
 */
export const mergePracticeAddress = (
  patientAddresses: AddressDto[],
  practiceAddress: AddressDto | undefined
): AddressDto[] => {
  if (!practiceAddress?.id) return patientAddresses;
  const practice: AddressDto = {
    ...practiceAddress,
    type: AddressDtoTypeEnum.Praxis,
  };
  // Replace rather than append any pre-existing practice entry, so the list
  // never offers two "Praxis" options for the same location.
  const rest = patientAddresses.filter(
    (a) => a.id !== practice.id && a.type !== AddressDtoTypeEnum.Praxis
  );
  return [practice, ...rest];
};

/** The home-visit address: the one flagged isVisit, falling back to isPrimary. */
export const pickHomeVisitAddress = (
  addresses: AddressDto[]
): AddressDto | undefined =>
  addresses.find((a) => a.isVisit) ?? addresses.find((a) => a.isPrimary);

/**
 * Pick a sensible default address the way NewRx does:
 *  - home visit → the visit address (else primary), else the practice,
 *  - praxis     → the practice/location address.
 *
 * The home-visit fallback to the practice matters because the result feeds a
 * hard requirement: an appointment without an address must not be creatable, so
 * "no visit address on file" has to land on something rather than on "".
 */
export const pickDefaultAddress = (
  addresses: AddressDto[],
  isHomeVisit: boolean | null | undefined
): AddressDto | undefined =>
  isPracticeRx(isHomeVisit)
    ? pickPracticeAddress(addresses)
    : pickHomeVisitAddress(addresses) ?? pickPracticeAddress(addresses);

/** Visit fee code (HB/HB2) implied by an address type, or null for Praxis. */
export const feeCodeForAddress = (
  address: AddressDto | undefined
): string | null => {
  const type = address?.type?.toLowerCase();
  if (type === "haus") return "HB";
  if (type === "heim") return "HB2";
  return null;
};
